import React from 'react';
import { Box, Typography } from '@material-ui/core';
import {
    CheckCircle,
    Error as ErrorIcon,
    HourglassEmpty,
} from '@material-ui/icons';

export interface BGVStatusItem {
    state: string;
    initiatedAt: string;
    status?: string;
    caseSeverity?: string;
}

interface StatusToken {
    background: string;
    border: string;
    color: string;
    dot: string;
    icon: React.ReactNode;
}

const STATUS_UI: Record<'GREEN' | 'RED' | 'DEFAULT', StatusToken> = {
    GREEN: {
        background: '#E8F5E9',
        border: '#4CAF50',
        color: '#1B5E20',
        dot: '#4CAF50',
        icon: <CheckCircle style={{ color: '#4CAF50', fontSize: 22 }} />,
    },
    RED: {
        background: '#FDECEA',
        border: '#F44336',
        color: '#B71C1C',
        dot: '#F44336',
        icon: <ErrorIcon style={{ color: '#F44336', fontSize: 22 }} />,
    },
    DEFAULT: {
        background: '#F1F3F6',
        border: 'rgba(38, 50, 56, 0.16)',
        color: '#14112d',
        dot: '#43419E',
        icon: <HourglassEmpty style={{ color: '#43419E', fontSize: 22 }} />,
    },
};

export const formatBGVTimestamp = (isoString?: string): string => {
    if (!isoString) return '';

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${day} ${month} ${year}, ${hours
        .toString()
        .padStart(2, '0')}:${minutes} ${ampm}`;
};

const mapSeverityToLabel = (caseSeverity?: string): string => {
    const s = (caseSeverity || '').trim().toLowerCase();
    if (s === 'bgv pending') return 'Background Verification Initiated';
    if (s === 'clear') return 'Verified';
    if (s === 'insufficient') return 'Insufficient';
    if (s === 'discrepant' || s === 'discrepancy') return 'Discrepancy';
    return caseSeverity || '--';
};

const mapSeverityToToken = (caseSeverity?: string): StatusToken => {
    const s = (caseSeverity || '').trim().toLowerCase();
    if (s === 'clear') return STATUS_UI.GREEN;
    if (s === 'insufficient' || s === 'discrepant' || s === 'discrepancy')
        return STATUS_UI.RED;
    return STATUS_UI.DEFAULT;
};

interface TimelineEvent {
    label: string;
    time: string;
    token: StatusToken;
}

const mapStatusToUI = (item: BGVStatusItem): TimelineEvent => {
    const { state, initiatedAt, status = '', caseSeverity = '' } = item;

    const normalizedState = (state || '').trim().toLowerCase();
    const normalizedStatus = (status || '').trim().toLowerCase();
    const formattedTime = formatBGVTimestamp(initiatedAt);

    if (normalizedStatus === 'interim' || normalizedStatus === 'final') {
        return {
            label: mapSeverityToLabel(caseSeverity),
            time: formattedTime,
            token: mapSeverityToToken(caseSeverity),
        };
    }

    if (normalizedState === 'initiated') {
        return {
            label: 'Background Verification Initiated',
            time: formattedTime,
            token: STATUS_UI.DEFAULT,
        };
    }

    return {
        label: state || 'Unknown Status',
        time: formattedTime,
        token: STATUS_UI.DEFAULT,
    };
};

const TimelineStep: React.FC<TimelineEvent & { showConnector: boolean }> = ({
    label,
    time,
    token,
    showConnector,
}) => (
    <Box style={{ display: 'flex', gap: 12 }}>
        {/* Rail: dot + connector */}
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Box
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: token.dot,
                    marginTop: 16,
                    flexShrink: 0,
                }}
            />
            {showConnector && (
                <Box
                    style={{
                        width: 2,
                        flexGrow: 1,
                        background: 'rgba(38, 50, 56, 0.24)',
                        margin: '4px 0',
                    }}
                />
            )}
        </Box>

        {/* Status pill */}
        <Box
            style={{
                flexGrow: 1,
                borderRadius: 8,
                border: `1px solid ${token.border}`,
                background: token.background,
                padding: '8px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: showConnector ? 16 : 0,
            }}
        >
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {token.icon}
                <Typography
                    style={{
                        color: token.color,
                        fontSize: 16,
                        fontWeight: 600,
                        fontFamily: 'Nunito',
                    }}
                >
                    {label}
                </Typography>
            </Box>

            {time && (
                <Typography
                    style={{
                        color: token.color,
                        fontSize: 14,
                        fontWeight: 600,
                    }}
                >
                    {time}
                </Typography>
            )}
        </Box>
    </Box>
);

interface BGVStatusV2Props {
    bgvStatusList?: BGVStatusItem[];
}

export default function BGVStatusV2({ bgvStatusList }: BGVStatusV2Props) {
    const list = Array.isArray(bgvStatusList) ? bgvStatusList : [];
    const events = list.map(mapStatusToUI);
    const hasTimeline = events.length > 0;
    const latest = hasTimeline ? events[events.length - 1] : null;

    return (
        <Box
            style={{
                borderRadius: 16,
                border: '1px solid rgba(38, 50, 56, 0.16)',
                padding: 16,
                backgroundColor: '#fff',
                width: '100%',
                marginBottom: 80,
                marginTop: 30,
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                }}
            >
                <Typography
                    variant="h6"
                    style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#14112d',
                    }}
                >
                    Background verification
                </Typography>

                {latest && (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            borderRadius: 16,
                            border: `1px solid ${latest.token.border}`,
                            background: latest.token.background,
                            padding: '4px 12px',
                        }}
                    >
                        {latest.token.icon}
                        <Typography
                            style={{
                                color: latest.token.color,
                                fontSize: 13,
                                fontWeight: 700,
                            }}
                        >
                            {latest.label}
                        </Typography>
                    </Box>
                )}
            </Box>

            {hasTimeline ? (
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    {events.map((event, index) => (
                        <TimelineStep
                            key={index}
                            label={event.label}
                            time={event.time}
                            token={event.token}
                            showConnector={index !== events.length - 1}
                        />
                    ))}
                </Box>
            ) : (
                <Typography
                    style={{
                        fontSize: 14,
                        color: '#4c4e64',
                        fontWeight: 500,
                    }}
                >
                    Background verification details will appear here once the
                    check is initiated.
                </Typography>
            )}
        </Box>
    );
}
